// A legacy grammar 2 selector read into a `Locator`. The recorded form is normalized once, here
// (journey/selector-normalize.ts), with each reference held in place. Without references the selector is `raw`. A
// reference is read by where it stands, so binding escapes it for that position instead of splicing it into selector
// syntax: a text engine body, a quoted CSS string, or a CSS identifier after `#` / `.`; anywhere else it is refused.
import { normalizeRecordedSelector } from '../journey/selector-normalize.ts'
import type { CssPart, Locator } from '../targeting/locator.ts'
import { parse } from '../journey/template.ts'
import { LegacyReadError } from './legacy-read-error.ts'
import type { Text, VarName } from './text.ts'

// A reference held in selector text: Unicode noncharacters (never in interchanged text) around its index.
const OPEN = '\uFDD0'
const CLOSE = '\uFDD1'
const HELD = new RegExp(`${OPEN}(\\d+)${CLOSE}`)
const ENCODED_HELD = new RegExp(`${encodeURIComponent(OPEN)}(\\d+)${encodeURIComponent(CLOSE)}`, 'gi')

export function legacyReadLocator(selector: string): Locator {
  const { segments } = parse(selector, 'selector')
  const names: VarName[] = []
  const marked = segments.map((segment) => typeof segment === 'string' ? segment : `${OPEN}${names.push(segment.ref) - 1}${CLOSE}`).join('')
  if (names.length && segments.some((segment) => typeof segment === 'string' && (segment.includes(OPEN) || segment.includes(CLOSE)))) refuse(selector, 'holds a Unicode noncharacter')
  // The href pass URL-encodes the path it keeps, the reference's delimiters included.
  const held = normalizeRecordedSelector(marked).replace(ENCODED_HELD, `${OPEN}$1${CLOSE}`)
  if (!names.length) return { raw: held }
  if (held.split(HELD).length !== 2 * names.length + 1) refuse(selector, 'names a variable the recorded-form normalization drops')
  const steps = splitSteps(held).map((step) => stepLocator(step, names, selector))
  return steps.length === 1 ? steps[0]! : { chain: steps }
}

// Playwright's pseudo-classes beyond CSS.
const PLAYWRIGHT_PSEUDO = /:(has-text|text|text-is|text-matches|visible|nth-match|left-of|right-of|above|below|near)\b/

/** `selector` iff it is one plain CSS selector: one step, the CSS engine, no Playwright pseudo-class. */
export function plainCss(selector: string): string | null {
  const steps = splitSteps(selector)
  return steps.length === 1 && engineOf(steps[0]!).name === 'css' && !PLAYWRIGHT_PSEUDO.test(unquotedSource(selector)) ? selector : null
}

// The CSS outside quoted strings, each escape dropped: where a pseudo-class can stand.
function unquotedSource(step: string): string {
  let source = ''
  for (let i = 0; i < step.length; i++) {
    const ch = step[i]!
    if (ch === '\\') i++
    else if (ch === '"' || ch === "'") i = stringEnd(step, i)
    else source += ch
  }
  return source
}

function stepLocator(step: string, names: VarName[], selector: string): Locator {
  if (!step.includes(OPEN)) return { raw: step }
  const { name, body } = engineOf(step)
  if (name === 'text') return legacyText(body, names, selector)
  if (name === 'internal:text') return internalText(body, names, selector)
  if (name === 'css') return { css: cssParts(body, names, selector) }
  return refuse(selector, `names a variable inside a ${name}= selector, which has no escaped form`)
}

// Playwright's engine of one step (`parseSelectorString`): `name=body`, a quoted text, xpath, else CSS.
function engineOf(step: string): { name: string; body: string } {
  const eq = step.indexOf('=')
  if (eq !== -1 && /^[a-zA-Z_0-9-+:*]+$/.test(step.slice(0, eq).trim())) return { name: step.slice(0, eq).trim(), body: step.slice(eq + 1) }
  if (step.length > 1 && (step[0] === '"' || step[0] === "'") && step.at(-1) === step[0]) return { name: 'text', body: step }
  if (/^\(*\/\//.test(step) || step.startsWith('..')) return { name: 'xpath', body: step }
  return { name: 'css', body: step }
}

// The legacy text engine: a quoted body (each backslash dropped) matches a text node; any other a case-insensitive substring.
function legacyText(body: string, names: VarName[], selector: string): Locator {
  if (body[0] === '/' && body.lastIndexOf('/') > 0) refuse(selector, 'names a variable inside a text regex')
  const quoted = body.length > 1 && (body[0] === '"' || body[0] === "'") && body.at(-1) === body[0]
  return quoted ? { text: unhold(body.slice(1, -1).replace(/\\([\s\S])/g, '$1'), names), match: 'node' } : { text: unhold(body, names) }
}

// `internal:text="…"` (JSON), flagged `i` for a case-insensitive substring, else the whole text.
function internalText(body: string, names: VarName[], selector: string): Locator {
  const quoted = /^("[\s\S]*")([si]?)$/.exec(body)
  if (!quoted) return refuse(selector, 'names a variable inside an unquoted or regex internal:text body')
  let text: unknown
  try {
    text = JSON.parse(quoted[1]!)
  } catch (error) {
    return refuse(selector, `holds an internal:text body that is not a JSON string (${String(error)})`)
  }
  return quoted[2] === 'i' ? { text: unhold(String(text), names) } : { text: unhold(String(text), names), match: 'exact' }
}

// The CSS step as literal source, quoted strings and identifiers. A string holding a reference is a `string` part; an
// unterminated one ends with the step, as CSS reads it. A reference outside strings must continue a `#` / `.` name.
function cssParts(step: string, names: VarName[], selector: string): CssPart[] {
  const parts: CssPart[] = []
  let source = ''
  const flush = () => {
    if (source) parts.push({ source })
    source = ''
  }
  for (let i = 0; i < step.length;) {
    const ch = step[i]!
    if (ch === '"' || ch === "'") {
      const end = stringEnd(step, i)
      const inner = step.slice(i + 1, end)
      if (inner.includes(OPEN)) {
        flush()
        parts.push({ string: unhold(cssUnescape(inner), names) })
      } else source += step.slice(i, end + 1)
      i = end + 1
    } else if (ch === OPEN) {
      if (!IDENT_TAIL.test(source)) refuse(selector, 'names a variable outside a quoted string or a #id / .class name')
      const close = step.indexOf(CLOSE, i) + 1
      flush()
      parts.push({ ident: unhold(step.slice(i, close), names) })
      i = close
    } else {
      const length = ch === '\\' ? 2 : 1
      source += step.slice(i, i + length)
      i += length
    }
  }
  flush()
  return parts
}

// A `#id` / `.class` name the source ends inside (escapes and non-ASCII included).
const IDENT_TAIL = /[#.](?:[\w-]|\\[\s\S]|[^\x00-\x7f])*$/

// The index of the quote closing the string opened at `start`, or the step's length when it stays open.
function stringEnd(step: string, start: number): number {
  for (let i = start + 1; i < step.length; i++) {
    if (step[i] === '\\') i++
    else if (step[i] === step[start]) return i
  }
  return step.length
}

// Playwright's step split (`parseSelectorString`): `>>` outside quotes (`"`, `'`, `` ` ``); quotes inside a non-empty
// legacy `text=` body open no string.
function splitSteps(selector: string): string[] {
  const steps: string[] = []
  let quote: string | undefined
  let start = 0
  const inTextBody = (i: number) => /^\s*text\s*=.+$/s.test(selector.slice(start, i))
  for (let i = 0; i < selector.length;) {
    const ch = selector[i]!
    if (ch === '\\' && i + 1 < selector.length) i += 2
    else if (ch === quote) {
      quote = undefined
      i++
    } else if (!quote && (ch === '"' || ch === "'" || ch === '`') && !inTextBody(i)) {
      quote = ch
      i++
    } else if (!quote && ch === '>' && selector[i + 1] === '>') {
      steps.push(selector.slice(start, i).trim())
      i += 2
      start = i
    } else i++
  }
  steps.push(selector.slice(start).trim())
  return steps
}

// CSS escapes: `\` + up to six hex digits (one following whitespace consumed) is that code point; `\` + newline
// nothing; `\` + any other character that character.
function cssUnescape(text: string): string {
  return text.replace(/\\(?:([0-9a-fA-F]{1,6})\s?|(\n)|([\s\S]))/g, (_match, hex?: string, newline?: string, ch?: string) =>
    hex ? String.fromCodePoint(Math.min(parseInt(hex, 16), 0x10ffff) || 0xfffd) : newline ? '' : ch ?? '')
}

// `text` with each held reference restored as a `{var}` part.
function unhold(text: string, names: VarName[]): Text {
  return text.split(HELD).flatMap((piece, i): Text => i % 2 ? [{ var: names[Number(piece)]! }] : piece ? [{ text: piece }] : [])
}

function refuse(selector: string, reason: string): never {
  throw new LegacyReadError('selector', `selector ${JSON.stringify(selector)} ${reason} — write the variable inside a quoted string ([data-test='$id'], :has-text('$name')) or a text= body`)
}
